import { useEffect, useRef } from "react";

/**
 * Calls `onLoadMore` once whenever a sentinel element scrolls into view
 * inside `rootRef`'s own scroll container — an IntersectionObserver, not a
 * scroll-position calculation re-run on every scroll event. That matters for
 * "smooth": a scroll handler has to recompute scrollHeight/scrollTop on
 * every tick (expensive on a long list, and easy to debounce into missing a
 * fast fling past the trigger point), where the browser's own observer fires
 * exactly once per crossing regardless of how fast the scroll was, with no
 * per-frame work on this side at all.
 *
 * Returns a ref to attach to the sentinel — an empty element placed after
 * the last row, before any "loading more…" footer.
 */
export function useInfiniteScrollSentinel({ rootRef, enabled, onLoadMore }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const root = rootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      // Fires a little before the sentinel is actually visible, so the next
      // page is already loading by the time a reader reaches the bottom.
      { root, rootMargin: "200px" },
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [rootRef, enabled, onLoadMore]);

  return sentinelRef;
}
